import type { ProspectResponse } from "../../../../../types/api";
import { prospectName, carryOverCount } from "./mergeFields";

interface SurvivorStepProps {
  prospects: ProspectResponse[];
  survivorId: string;
  onSelect: (prospectId: string) => void;
  /** Records the coach has taken out of this merge. */
  excludedIds: Set<string>;
  /** Toggle a non-survivor record in or out of the merge. */
  onToggleInclude: (prospectId: string) => void;
}

/**
 * Step 1 — pick the surviving profile and confirm who is merging into it.
 *
 * Two decisions live on this step. The radio pins the survivor, which is
 * implicitly excluded from the loser set (the modal derives losers as the
 * included non-survivors), so the survivor can never also be merged away. The
 * per-row checkbox drops a record from the merge entirely: duplicate detection is
 * a guess, and a group can pick up a genuinely different player, so leaving
 * someone out has to be possible without abandoning the whole merge.
 */
export const SurvivorStep = ({
  prospects,
  survivorId,
  onSelect,
  excludedIds,
  onToggleInclude,
}: SurvivorStepProps) => {
  const excludedCount = prospects.filter(
    (p) => p.id !== survivorId && excludedIds.has(p.id)
  ).length;
  const includedLosers = prospects.length - 1 - excludedCount;

  return (
    <fieldset className="merge-modal__fieldset">
      <legend className="merge-modal__legend">
        Choose the profile to keep
      </legend>
      <p className="merge-modal__hint">
        All notes, scouting data, and media from the other players will move onto
        this profile. The others will be retired. Uncheck anyone who is not the
        same player — they stay exactly as they are.
      </p>
      <ul className="merge-modal__survivor-list">
        {prospects.map((p) => {
          const carry = carryOverCount(p);
          const isSurvivor = survivorId === p.id;
          const excluded = !isSurvivor && excludedIds.has(p.id);
          return (
            <li
              key={p.id}
              className={`merge-modal__survivor-item${
                excluded ? " merge-modal__survivor-item--excluded" : ""
              }`}
            >
              <label className="merge-modal__radio-label">
                <input
                  type="radio"
                  name="merge-survivor"
                  value={p.id}
                  checked={isSurvivor}
                  onChange={() => onSelect(p.id)}
                  data-testid={`survivor-radio-${p.id}`}
                />
                <span className="merge-modal__survivor-name">{prospectName(p)}</span>
                <span className="merge-modal__survivor-meta">
                  {p.position || "No position"}
                  {p.gradYear ? ` · ${p.gradYear}` : ""}
                  {carry > 0 ? ` · ${carry} note${carry !== 1 ? "s" : ""}` : ""}
                  {excluded ? " · Not merging, left as is" : ""}
                </span>
              </label>

              {/* The survivor has nothing to include or exclude — it is the
                  profile everything lands on. */}
              {!isSurvivor && (
                <label className="merge-modal__include-label">
                  <input
                    type="checkbox"
                    checked={!excluded}
                    onChange={() => onToggleInclude(p.id)}
                    aria-label={`Include ${prospectName(p)} in this merge`}
                    data-testid={`merge-include-${p.id}`}
                  />
                  <span className="merge-modal__include-text">Merge in</span>
                </label>
              )}
            </li>
          );
        })}
      </ul>

      {includedLosers === 0 ? (
        <p className="merge-modal__blocked" role="alert" data-testid="merge-nothing-to-merge">
          Everyone else is excluded, so there is nothing to merge. Include at least
          one player, or close this and dismiss the group instead.
        </p>
      ) : excludedCount > 0 ? (
        <p className="merge-modal__hint" data-testid="merge-excluded-notice">
          {excludedCount} {excludedCount === 1 ? "player is" : "players are"} excluded
          and will not be changed.
        </p>
      ) : null}
    </fieldset>
  );
};
